/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { useAPICallAsMutation } from '../hooks';
import { IBook, PaginatedResponse, Params } from '../types';
import {
  getBookActivities,
  getBookCategories,
  getBooks,
  getFavoritedBooks,
  getMyBooks,
  getExchangeableBooks,
  getReceivedBooks,
  getMyBooksDetail,
  getReadingBooks,
  getReceivedBooksMonth,
  getBooksMonth,
  favoriteBook,
  getStreamBook,
  unFavoriteBook,
  setReadingBook,
  getReceivedBooksMonthBestSellers,
  getBooksDetail,
  getMonthBestSellers,
  getReadingBooksDetail,
} from './api';

export enum BookstoreHook {
  Activities = 'activities',
  Books = 'books',
  FeaturedBooks = 'featured-books',
  Categories = 'categories',
  MyBooks = 'my-books',
  GetBook = 'get-book',
  GetExchangeableBooks = 'get-exchangeable-books',
  GetMyBooksDetail = 'get-my-books-detail',
  GetReadingBooksDetail = 'get-reading-books-detail',
  Received = 'received',
  ReceivedMothly = 'received-monthly',
  BestSellersMothlyReceived = 'received-monthly-best-sellers',
  BestSellersMothly = 'monthly-best-sellers',
}

export function useBookCategories() {
  return useQuery([BookstoreHook.Categories], () => getBookCategories());
}

export function useBookActivities() {
  return useQuery([BookstoreHook.Activities], () => getBookActivities());
}

export function useBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.Books, params], () => getBooks(params));
}

export function useGetBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getBooks({ ...params } as Record<string, string>), options);
}

export function useMyBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.MyBooks, params], () => getMyBooks(params));
}

export function useExchangeableBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getExchangeableBooks(params), options);
}

export function useGetMyBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getMyBooks(params as Record<string, string>), options);
}

export function useMyReadingBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useAPICallAsMutation(getReadingBooks, options);
}

export function useGetAudioBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getBooks({ ...params, audiobook: 'true' }), options);
}

export function useGetFavoritedBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useAPICallAsMutation(getFavoritedBooks, options);
}

export function useReceivedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.Received, params], () => getReceivedBooks(params));
}

export function useReceivedMothly(params?: Record<string, string>) {
  return useQuery([BookstoreHook.ReceivedMothly, params], () => getReceivedBooksMonth(params));
}

export function useMothlyBestSellers(params?: Record<string, string>) {
  return useQuery([BookstoreHook.BestSellersMothly, params], () => getMonthBestSellers(params));
}

export function useReceivedMothlyBestSellers(params?: Record<string, string | boolean>) {
  const stringParams = params
    ? Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)]))
    : undefined;
  return useQuery([BookstoreHook.BestSellersMothlyReceived, stringParams], () => getReceivedBooksMonthBestSellers(stringParams));
}

export function useGetBookDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetMyBooksDetail, bookId], () => getBooksDetail(bookId), options);
}

export function useGetReadingBooksDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetReadingBooksDetail, bookId], () => getReadingBooksDetail(bookId), options);
}


export function useGetMyBooksDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetMyBooksDetail, bookId], () => getMyBooksDetail(bookId), options);
}

export function useFeaturedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.FeaturedBooks, params], () => getBooksMonth(params));
}

export function useUnFavoriteBookMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => unFavoriteBook(bookId).then((response) => response.data as IBook), options);
}

export function useFavoriteBookMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => favoriteBook(bookId).then((response) => response.data as IBook), options);
}

export function useDownloadMutation(
  options: UseMutationOptions<
    {
      book: Blob;
      contentType: string;
    },
    unknown,
    number
  >
) {
  return useMutation((bookId: number) => getStreamBook(bookId).then((b) => b), options);
}

export function useGetBookReadingMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => setReadingBook(bookId), options);
}
